import React from 'react';

/** Loopback port the NextCompli agent serves when `run` is active. */
export const LOCAL_AGENT_PRESENCE_PORT = 18765;
export const LOCAL_AGENT_PRESENCE_URL = `http://127.0.0.1:${LOCAL_AGENT_PRESENCE_PORT}/v1/presence`;

export type LocalAgentPresence = {
  status: 'checking' | 'found' | 'missing' | 'blocked';
  hostname?: string;
  cloud?: string;
  runtime?: string;
  installId?: string;
  detail?: string;
};

type PresenceBody = {
  ok?: boolean;
  hostname?: string;
  cloud?: string;
  runtime?: string;
  install_id?: string;
};

/**
 * Probe whether a NextCompli agent is answering on this browser's computer.
 * Requires the agent `run` loop to expose loopback presence (CORS for console origins).
 * Browser security blocks reading other tabs' environments — only loopback works.
 */
export function useLocalAgentPresence(enabled: boolean): LocalAgentPresence {
  const [state, setState] = React.useState<LocalAgentPresence>({ status: 'checking' });

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setState({ status: 'missing', detail: 'off' });
      return undefined;
    }
    let cancelled = false;
    const probe = async () => {
      setState((current) => (current.status === 'found' ? current : { status: 'checking' }));
      try {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 900);
        const response = await fetch(LOCAL_AGENT_PRESENCE_URL, {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store',
        });
        window.clearTimeout(timer);
        if (cancelled) return;
        if (!response.ok) {
          setState({ status: 'missing', detail: `HTTP ${response.status}` });
          return;
        }
        const body = await response.json().catch(() => ({})) as PresenceBody;
        if (cancelled) return;
        if (body.ok === false) {
          setState({ status: 'missing' });
          return;
        }
        setState({
          status: 'found',
          hostname: String(body.hostname || '').trim() || undefined,
          cloud: String(body.cloud || '').trim() || undefined,
          runtime: String(body.runtime || '').trim() || undefined,
          installId: String(body.install_id || '').trim() || undefined,
        });
      } catch (error) {
        if (cancelled) return;
        const name = error instanceof Error ? error.name : '';
        // Abort / TypeError / Failed to fetch → nothing listening (or CORS blocked old builds).
        setState({
          status: name === 'AbortError' ? 'missing' : 'missing',
          detail: name || 'unreachable',
        });
      }
    };
    void probe();
    const interval = window.setInterval(() => {
      void probe();
    }, 45_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [enabled]);

  return state;
}
